/**
 * useWlPartnerLogos — gestão de logomarcas de parceiros do painel WL.
 *
 * Toda I/O passa pelas RPCs `wl_admin_list_partner_logos`,
 * `wl_upsert_partner_logo`, `wl_delete_partner_logo` e `wl_reorder_partner_logos`.
 * Upload de imagens vai para o bucket `anexos` em `system/partners/{uuid}.{ext}`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { createLogger } from "@/lib/logger";

const logger = createLogger("WlPartnerLogos");
const ADMIN_KEY = ["wl", "partner-logos", "admin"] as const;
const PUBLIC_KEY = ["wl", "partner-logos", "public"] as const;

export interface WlPartnerLogo {
  id: string;
  name: string;
  logo_url: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useWlPartnerLogos() {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ADMIN_KEY,
    queryFn: async (): Promise<WlPartnerLogo[]> => {
      const { data, error } = await supabase.rpc("wl_admin_list_partner_logos");
      if (error) throw error;
      return (data ?? []) as WlPartnerLogo[];
    },
    staleTime: 60_000,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ADMIN_KEY });
    qc.invalidateQueries({ queryKey: PUBLIC_KEY });
  };

  /** Upload do arquivo no storage. Retorna URL pública (com cache-bust). */
  const uploadLogo = async (file: File): Promise<{ url: string; path: string }> => {
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const id = crypto.randomUUID();
    const path = `system/partners/${id}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("anexos")
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      logger.error("Erro no upload do logo de parceiro", uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage.from("anexos").getPublicUrl(path);
    return { url: `${data.publicUrl}?t=${Date.now()}`, path };
  };

  const upsert = useMutation({
    mutationFn: async (input: {
      id?: string | null;
      name: string;
      logo_url: string;
      display_order?: number;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase.rpc("wl_upsert_partner_logo", {
        p_id: input.id ?? null,
        p_name: input.name,
        p_logo_url: input.logo_url,
        p_display_order: input.display_order ?? 0,
        p_is_active: input.is_active ?? true,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Parceiro salvo" });
    },
    onError: (err: Error) =>
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("wl_delete_partner_logo", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Parceiro removido" });
    },
    onError: (err: Error) =>
      toast({ title: "Erro ao remover", description: err.message, variant: "destructive" }),
  });

  const reorder = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.rpc("wl_reorder_partner_logos", { p_ids: ids });
      if (error) throw error;
    },
    onSuccess: () => invalidateAll(),
  });

  return {
    logos: list.data ?? [],
    isLoading: list.isLoading,
    uploadLogo,
    upsert,
    remove,
    reorder,
  };
}

/** Hook público para a página de planos do tenant (sem permissões WL). */
export function useWlPartnerLogosPublic() {
  return useQuery({
    queryKey: PUBLIC_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("wl_list_partner_logos");
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; name: string; logo_url: string }>;
    },
    staleTime: 10 * 60 * 1000,
  });
}
